import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
} from "react-native";
import { useTranslation } from 'react-i18next';
import { loadCountries } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import FlatListDataComponent from "../../../common/FlatListDataComponent";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import Colors from "../../../../../constants/colors";
import { capturePosthogEvent } from "../../../../../utils/analytics";

type StateItem = {
  stateid: number;
  stateAbbrivation: string;
  stateName: string
};

type ChooseStateOfIdStepProps = {
  onCountrySelected?: () => void;
  testID?: string | undefined;
};

export default function ChooseStateOfIdStep({ onCountrySelected, testID }: ChooseStateOfIdStepProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const storedIdStateName = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdStateName as string | null | undefined);
  const [selectedState, setSelectedState] = useState<string | null>(
    () => (useSessionStore.getState() as any).selectedIdPhotoIdStateName ?? null
  );
  const [states, setStates] = useState<StateItem[]>([]);
  const setSelectedIdPhotoIdStateAbbr = useSessionStore((s: SessionState) => s.setSelectedIdPhotoIdStateAbbr);
  const setSelectedIdPhotoIdStateName = useSessionStore((s: SessionState) => s.setSelectedIdPhotoIdStateName);
  const selectedIdPhotoIdCountry = useSessionStore((s: SessionState) => (s as any).selectedIdPhotoIdCountry);

  useEffect(() => {
    setSelectedState(storedIdStateName ?? null);
  }, [storedIdStateName]);

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const result = await loadCountries();
        const list = (result?.data ?? []) as any[];
        const targetCountryCode = selectedIdPhotoIdCountry || 'USA';
        const country = list.find((c) => c.CountryCode === targetCountryCode);
        const countryStates: StateItem[] = Array.isArray(country?.States) ? country.States : [];
        setStates(countryStates);
      } catch (e) {
        console.error('Failed to load states', e);
      }
    };
    fetchStates();
  }, [selectedIdPhotoIdCountry]);

  // Filter states by search
  const filteredState: StateItem[] = useMemo(() => {
    const q = search.toLowerCase();
    return states.filter((s) => (s.stateName || '').toLowerCase().includes(q));
  }, [states, search]);

  const onSelectState = (item: StateItem) => {
    setSelectedState(item.stateName);
    setSelectedIdPhotoIdStateName?.(item.stateName);
    setSelectedIdPhotoIdStateAbbr?.(item.stateAbbrivation);
    // give time for radio to show
    setTimeout(() => {
      onCountrySelected?.();
    }, 200);
    capturePosthogEvent('Send Flow - Sender ID State Capture Step', {
      'data-mg-state': item.stateName
    });
  }

  return (
    <View style={styles.containerSticky} testID={testID}>
      <FlatListDataComponent
        stickyHeader
        title={t("YOUR_STATE_OF_ID")}
        subTitle={t("SELECT_STATE_OF_ISSUE")}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData={filteredState}
        searchTestID="stateOfIdSearchInput"
        flatListTestID="stateOfIdList"
        renderItem={(item) => (
          <TouchableOpacity
            testID={`stateOfIdOption_${item?.stateAbbrivation}`}
            style={styles.countryRow}
            onPress={() => {
              onSelectState(item);
            }}
          >
            <Text
              style={styles.countryName}
              numberOfLines={2}
              ellipsizeMode="tail"
            >
              {item?.stateName} ({item?.stateAbbrivation})
            </Text>
            <View style={styles.radioButton}>
              {selectedState === item?.stateName && (
                <RadioSelectedIcon width={20} height={20}/>
              )}
            </View>
          </TouchableOpacity>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  containerSticky: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: 'space-between'
  },
  countryName: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.darkGrey,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.radioButtonFilled
  },
});
